import { AbstractService } from "./ApiClient";
import { ApiRoutes } from "@typingsGenerator/ApiRoutes";
import UserRegistrationDto = Cloud.RavenDB.Core.Dto.Users.UserRegistrationDto;
import LoginDto = Cloud.RavenDB.Core.Dto.Users.LoginDto;
import UserInfoDto = Cloud.RavenDB.Core.Dto.Users.UserInfoDto;
import DashboardUserInfoDto = Cloud.RavenDB.Core.Dto.Users.DashboardUserInfoDto;
import HostedPaymentFieldsTokenResultDto = Cloud.RavenDB.Core.Dto.Orders.HostedPaymentFieldsTokenResultDto;
import AccountInfoDto = Cloud.RavenDB.Core.Dto.Users.AccountInfoDto;
import AccountBillingInfoDto = Cloud.RavenDB.Core.Dto.Users.AccountBillingInfoDto;
import AccountUsersInfoDto = Cloud.RavenDB.Core.Dto.Users.AccountUsersInfoDto;
import InviteUserDto = Cloud.RavenDB.Core.Dto.Users.InviteUserDto;
import DeleteLoginFromAccountDto = Cloud.RavenDB.Core.Dto.Users.DeleteLoginFromAccountDto;
import AccountInvitationInformationDto = Cloud.RavenDB.Core.Dto.Users.AccountInvitationInformationDto;
import CreateCloudLoginViaInvitationDto = Cloud.RavenDB.Core.Dto.Users.CreateCloudLoginViaInvitationDto;
import EncryptedMagicLinkDto = Cloud.RavenDB.Core.Dto.Users.EncryptedMagicLinkDto;
import CancelPendingInvitationDto = Cloud.RavenDB.Core.Dto.Users.CancelPendingInvitationDto;
import CheckEmailAddressAvailabilityRequestDto = Cloud.RavenDB.Core.Dto.Users.CheckEmailAddressAvailabilityRequestDto;
import CheckEmailAddressAvailabilityResponseDto = Cloud.RavenDB.Core.Dto.Users.CheckEmailAddressAvailabilityResponseDto;

export default class UserService extends AbstractService {
    createUser = async (dto: UserRegistrationDto): Promise<void> => {
        await this.post<UserRegistrationDto, void>(ApiRoutes.users.createUser(), dto);
    };

    isEmailAddressAlreadyUsed = async (emailAddress: string): Promise<boolean> => {
        const response = await this.post<
            CheckEmailAddressAvailabilityRequestDto,
            CheckEmailAddressAvailabilityResponseDto
        >(ApiRoutes.users.isEmailAddressAlreadyUsed(), { email: emailAddress });
        return response.data.alreadyUsed;
    };

    getAccountInfo = async (domainName: string) => {
        const response = await this.query<AccountInfoDto>(ApiRoutes.users.getAccountInfo({ domainName }));
        return response.data;
    };

    getAccountBillingInfo = async (domainName: string) => {
        const response = await this.query<AccountBillingInfoDto>(ApiRoutes.users.getAccountBillingInfo({ domainName }));
        return response.data;
    };

    getAccountUsersInfoDto = async (domainName: string) => {
        const response = await this.query<AccountUsersInfoDto>(ApiRoutes.users.getAccountUsersInfo({ domainName }));
        return response.data;
    };

    requestLogin = async (dto: LoginDto): Promise<void> => {
        await this.post<LoginDto, void>(ApiRoutes.login.loginUser(), dto);
    };

    logout = async (): Promise<void> => {
        await this.post(ApiRoutes.login.logout());
    };

    getUserInfo = async (): Promise<UserInfoDto> => {
        const response = await this.query<UserInfoDto>(ApiRoutes.users.getUserInfo());
        return response.data;
    };

    loadPfToken = async (): Promise<HostedPaymentFieldsTokenResultDto> => {
        const response = await this.query<HostedPaymentFieldsTokenResultDto>(ApiRoutes.users.getToken());
        return response.data;
    };

    inviteUser = async (domainName: string, dto: InviteUserDto): Promise<void> => {
        await this.post<InviteUserDto, void>(ApiRoutes.users.inviteUser({ domainName }), dto);
    };

    cancelPendingInvitation = async (domainName: string, dto: CancelPendingInvitationDto): Promise<void> => {
        await this.post<CancelPendingInvitationDto, void>(ApiRoutes.users.cancelPendingInvitation({ domainName }), dto);
    };

    confirmInvitation = async (domainName: string): Promise<void> => {
        await this.post<void>(ApiRoutes.users.confirmInvitation({ domainName }));
    };

    deleteLoginFromAccount = async (domainName: string, dto: DeleteLoginFromAccountDto) => {
        await this.deleteWithPayload<DeleteLoginFromAccountDto, void>(
            ApiRoutes.users.unlinkUserFromAccount({ domainName }),
            dto
        );
    };

    loadInvitationInfo = async (link: string) => {
        const response = await this.query<{ link: string }, AccountInvitationInformationDto>(
            ApiRoutes.invitation.getInvitationInfo(),
            {
                link,
            }
        );
        return response.data;
    };

    createUserViaInvitation = async (dto: CreateCloudLoginViaInvitationDto) => {
        const response = await this.post<CreateCloudLoginViaInvitationDto, EncryptedMagicLinkDto>(
            ApiRoutes.users.createUserViaInvitation(),
            dto
        );

        return response.data;
    };

    getDashboardUserInfo = async (domainName: string): Promise<DashboardUserInfoDto> => {
        const response = await this.query<DashboardUserInfoDto>(ApiRoutes.users.getDashboardUserInfo({ domainName }));
        return response.data;
    };
}
